import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Yup from 'yup';
import { Chip } from '@mui/material';
import Dropzone from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { MdClose } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';

import { LinkTypes } from 'helpers';
import { makeFormStyles } from '../styles';
import SetDirtyForm from 'forms/SetDirtyForm';
import { SelectionAutocomplete } from 'components';
import { FormActions } from 'helpers/form-action';
import { FormTextField } from 'components/TextField';
import { closePanel } from 'features/common/formSlice';
import { panelStyles } from 'GeminiViewerComponent/components/styles';
import { snackbarHandler } from 'GeminiViewerComponent/helpers/snackbar-handler';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { selectDefaultTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { LoadingLogo } from 'GeminiViewerComponent/components/LoadingLogo/LoadingLogo';
import { createTool, fetchToolById, updateTool } from 'features/tools/toolSlice';
import {
    getProceduresPicklist,
    selectProceduresPicklist,
    selectProceduresPicklistLoadingStatus,
} from 'features/picklists/pickListsSlice';
import {
    setProgress,
    activateLoading,
    deactivateLoading,
} from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import {
    createToolCategory,
    fetchToolCategoriesPage,
    getToolsCategoryStatus,
    selectToolsCategories,
} from 'features/tools/toolsCategorySlice';
import { createLocation, fetchLocationsPage, getLocationStatus, selectLocations } from 'features/tools/locationSlice';

const INIT_VALUES = {
    display_name: '',
    description: '',
    sku: '',
    location_ids: [],
    procedure_ids: [],
    tool_category_id: 0,
    image_url: '',
    image: null,
};

const ToolForm = ({ formAction, toolId }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [loaded, setLoaded] = useState(formAction.id === FormActions.Create.id ? true : false);
    const theme = useSelector(selectDefaultTheme);
    const classes = makeFormStyles(theme);
    const panelClasses = panelStyles(theme);
    const [initValues, setInitValues] = useState({ ...INIT_VALUES });
    const toolNameRef = useRef(null);

    useEffect(() => {
        if (formAction.id === FormActions.Edit.id) {
            const fetchData = async () => {
                let objectData = await dispatch(fetchToolById(toolId));
                let fetchedObject = objectData.payload;
                setInitValues({
                    display_name: fetchedObject?.display_name,
                    tool_category_id: fetchedObject?.tool_category_id,
                    sku: fetchedObject?.sku,
                    location_ids: Array.isArray(fetchedObject?.locations)
                        ? fetchedObject?.locations?.map((loc) => loc?.tool_location_id)
                        : [],
                    procedure_ids: Array.isArray(fetchedObject?.procedures)
                        ? fetchedObject?.procedures?.map((proc) => proc?.procedure_id)
                        : [],
                    description: fetchedObject?.description,
                    image_url: fetchedObject?.image_url,
                    image: null,
                });
                setLoaded(true);
            };
            fetchData();
        } else {
            setInitValues({ ...INIT_VALUES });
        }
    }, [toolId]);

    const handleSubmit = async (values, setSubmitting, resetForm) => {
        dispatch(activateLoading({ showProgress: true }));
        let resultAction = null;
        switch (formAction.id) {
            case FormActions.Edit.id:
                if (values?.image) {
                    delete values?.image_url;
                }
                resultAction = await dispatch(updateTool({ tool_id: toolId, ...values, dispatch }));
                if (!resultAction.error) {
                    dispatch(closePanel({ formKey: 'toolForm' }));
                }
                break;
            case FormActions.Create.id:
                dispatch(
                    setProgress({
                        progress: 0,
                        label: 'Step 1 of 2: Uploading',
                        noAnimation: true,
                    })
                );
                resultAction = await dispatch(createTool({ ...values, dispatch }));
                if (!resultAction.error) {
                    resetForm();
                    if (toolNameRef.current) {
                        toolNameRef.current.focus();
                    }
                }
                break;
            default:
                break;
        }

        dispatch(deactivateLoading());
        const { message, variant } = snackbarHandler(resultAction.meta.requestStatus, formAction.label);
        enqueueSnackbar(message, {
            action: (key) => <SnackbarDismiss key={key} />,
            variant: variant,
        });
        setSubmitting(false);
    };

    const onDrop = useCallback(({ file, setFieldValue }) => {
        setFieldValue('image', file);
        const reader = new FileReader();

        reader.onabort = () => console.log('file reading was aborted');
        reader.onerror = () => console.log('file reading has failed');
        reader.onload = (e) => {
            setFieldValue('image_url', e.target.result);
        };
        reader.readAsDataURL(file);
    }, []);

    const ToolsFormProps = [
        {
            inputRef: toolNameRef,
            autoFocus: true,
            label: 'Tool Name',
            name: 'display_name',
            placeholder: 'Tool Name',
        },
        {
            label: 'Description',
            name: 'description',
            placeholder: 'Description',
        },
        {
            label: 'SKU',
            name: 'sku',
            placeholder: 'SKU',
        },
    ];

    return !loaded ? (
        <LoadingLogo styleClassName={classes.loadingSvg} />
    ) : (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...initValues }}
            validationSchema={Yup.object({
                display_name: Yup.string().required('Required').max(150, 'Must be 150 characters or less'),
                description: Yup.string().max(255, 'Must be 255 characters or less'),
                location_ids: Yup.array(),
                procedure_ids: Yup.array(),
                sku: Yup.string().max(255, 'Must be 255 characters or less'),
                tool_category_id: Yup.number().notOneOf([0, null, ''], 'Required').required('Required'),
            })}
            onSubmit={(values, { setSubmitting, resetForm }) => {
                handleSubmit(values, setSubmitting, resetForm);
            }}
        >
            {({ isSubmitting, values, errors, touched }) => (
                <Form className={classes.form}>
                    <div className={classes.formHeader}>Tool Details</div>
                    {ToolsFormProps.map((field, index) => (
                        <FormTextField key={index} {...field} />
                    ))}
                    <Field className={classes.input} name="tool_category_id">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Category"
                                placeholder="Category"
                                keyProperty="tool_category_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectToolsCategories}
                                entityStatusSelector={getToolsCategoryStatus}
                                fetchEntityPage={fetchToolCategoriesPage}
                                formField={'tool_category_id'}
                                multiSelection={false}
                                enableCreatable
                                creatableDialogTitle={'tool category'}
                                creatableDialogFieldLabel={'Category'}
                                creatableDispatch={createToolCategory}
                                fetchParams={{}}
                                creatableParams={{}}
                                touched={touched['tool_category_id']}
                                errorMessage={errors['tool_category_id']}
                                fieldValidationLength={150}
                            />
                        )}
                    </Field>
                    <div className={classes.formErrorDiv}>
                        <ErrorMessage name="tool_category_id" component="div" />
                    </div>
                    <Field className={classes.input} name="location_ids">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Locations"
                                placeholder="Locations"
                                keyProperty="tool_location_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectLocations}
                                entityStatusSelector={getLocationStatus}
                                fetchEntityPage={fetchLocationsPage}
                                formField={'location_ids'}
                                multiSelection={true}
                                enableCreatable
                                creatableDialogTitle={'Locations'}
                                creatableDialogFieldLabel={'Locations'}
                                creatableDispatch={createLocation}
                                fetchParams={{}}
                                creatableParams={{}}
                                touched={touched['location_ids']}
                                errorMessage={errors['location_ids']}
                                fieldValidationLength={150}
                            />
                        )}
                    </Field>
                    <div className={classes.formErrorDiv}>
                        <ErrorMessage name="location_ids" component="div" />
                    </div>
                    <Field className={classes.input} name="procedure_ids">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <SelectionAutocomplete
                                title="Procedures"
                                placeholder="Procedures"
                                keyProperty="procedure_id"
                                nameProperty="display_name"
                                entityIds={value}
                                setEntityIds={setFieldValue}
                                entitySelector={selectProceduresPicklist}
                                fetchEntityPage={getProceduresPicklist}
                                formField={'procedure_ids'}
                                multiSelection={true}
                                entityStatusSelector={selectProceduresPicklistLoadingStatus}
                                touched={touched['procedure_ids']}
                                errorMessage={errors['procedure_ids']}
                            />
                        )}
                    </Field>
                    <div className={classes.formErrorDiv}>
                        <ErrorMessage name="procedure_ids" component="div" />
                    </div>
                    <Field className={classes.input} name="image" type="file">
                        {({ field: { value }, form: { setFieldValue } }) => (
                            <div className={panelClasses.dropzoneContainer}>
                                <Dropzone
                                    accept={LinkTypes.Image.contentTypes.map((at) => at.extension)}
                                    maxFiles={1}
                                    maxSize={LinkTypes.Image.fileSizeLimit}
                                    multiple={false}
                                    onDropAccepted={(files) => {
                                        onDrop({
                                            file: files[0],
                                            setFieldValue: setFieldValue,
                                        });
                                    }}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <div
                                            {...getRootProps()}
                                            className={panelClasses.dropzoneArea}
                                            style={{
                                                backgroundImage: `url(${values?.image_url})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: 'contain',
                                                backgroundPosition: 'center center',
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            {!values.image_url && (
                                                <div className={panelClasses.dropzoneText}>Drop Tool Icon Here</div>
                                            )}
                                        </div>
                                    )}
                                </Dropzone>
                                <div className={panelClasses.previewContainer}>
                                    {(value && Object.keys(value).length > 0) || values?.image_url ? (
                                        <Chip
                                            className={panelClasses.previewUploadFileName}
                                            color="secondary"
                                            size="small"
                                            deleteIcon={<MdClose className="react-icon" />}
                                            onDelete={() => {
                                                setFieldValue('image', null);
                                                setFieldValue('image_url', '');
                                            }}
                                            label={value?.name}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </Field>
                    <SetDirtyForm />
                    <button className={classes.submit} type="submit" disabled={isSubmitting}>
                        {formAction.buttonLabel}
                    </button>
                </Form>
            )}
        </Formik>
    );
};

export { ToolForm };
